import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { apiTenant } from '../../../services/Tenant/ApiTenant'
import { Apartment, Receipt, ReceiptLong } from '@mui/icons-material'
import Context from '../../../contexts/Context'

const formatBRL = (centavos) => {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const TenantDashboard = () => {
  const navigate = useNavigate()
  const { tenant } = useContext(Context)
  const [units, setUnits] = useState([])
  const [pendingInvoices, setPendingInvoices] = useState([])
  const [pendingCharges, setPendingCharges] = useState([])
  const [loading, setLoading] = useState(true)

  const toArray = (data) => {
    if (Array.isArray(data)) return data
    if (data?.results && Array.isArray(data.results)) return data.results
    return []
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [unitsRes, invoicesRes, chargesRes] = await Promise.all([
          apiTenant.get('/tenant/units/'),
          apiTenant.get('/tenant/invoices/', { params: { status: 'pending' } }),
          apiTenant.get('/tenant/charges/'),
        ])
        setUnits(toArray(unitsRes.data))
        setPendingInvoices(toArray(invoicesRes.data))
        setPendingCharges(toArray(chargesRes.data))
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const pendingTotal = pendingInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0)
  const overdueInvoices = pendingInvoices.filter(inv => inv.status === 'overdue')
  const pendingChargesFiltered = pendingCharges.filter(c => c.status === 'pending' || c.status === 'overdue')

  return (
    <>
      <Helmet>
        <title>Portal do Cliente - Bloco B3</title>
      </Helmet>
      <Container maxWidth={false}>
        <Box sx={{ marginY: '1em', pb: 1 }}>
          <Typography variant="h5">
            Olá, {tenant?.units?.[0]?.name || 'Cliente'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Bem-vindo ao Portal do Cliente
          </Typography>
        </Box>

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3].map(i => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={160} animation="pulse"/>
              </Grid>
            ))}
          </Grid>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Receipt color="primary" sx={{ mr: 1 }}/>
                      <Typography variant="h6">Faturas Pendentes</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {pendingInvoices?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Total: {formatBRL(pendingTotal)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {overdueInvoices.length > 0 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid', borderColor: 'error.main' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Receipt color="error" sx={{ mr: 1 }}/>
                        <Typography variant="h6">Faturas Atrasadas</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        {overdueInvoices.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Total: {formatBRL(overdueInvoices.reduce((s, i) => s + (i.total || 0), 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {pendingChargesFiltered.length > 0 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ReceiptLong color="primary" sx={{ mr: 1 }}/>
                        <Typography variant="h6">Cobranças Pendentes</Typography>
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        {pendingChargesFiltered.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Total: {formatBRL(pendingChargesFiltered.reduce((s, c) => s + (c.total || 0), 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Units */}
            <Typography variant="h6" sx={{ mb: 2 }}>Seus Imóveis</Typography>
            <Grid container spacing={3}>
              {units?.map((unit) => (
                <Grid item xs={12} sm={6} md={4} key={unit.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Apartment color="secondary" sx={{ mr: 1 }}/>
                        <Typography variant="h6">
                          Unidade {unit.number}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {unit.name}
                      </Typography>
                      <Chip
                        label={unit.condo_name}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Quick Actions */}
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Card
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, flex: 1, transition: 'box-shadow .2s' }}
                onClick={() => navigate('/area-cliente/faturas/')}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Receipt color="primary"/>
                  <Typography>Ver todas as faturas</Typography>
                </CardContent>
              </Card>
              <Card
                sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 }, flex: 1, transition: 'box-shadow .2s' }}
                onClick={() => navigate('/area-cliente/cobrancas/')}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ReceiptLong color="primary"/>
                  <Typography>Ver todas as cobranças</Typography>
                </CardContent>
              </Card>
            </Box>
          </>
        )}
      </Container>
    </>
  )
}

export default TenantDashboard
